import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Eye, Loader2 } from "lucide-react";

const LANG_LABEL = { italiano: "Italiano", inglese: "Inglese", francese: "Francese", spagnolo: "Spagnolo", tedesco: "Tedesco" };

/**
 * Anteprima WYSIWYG: il backend assembla l'email con la STESSA funzione deterministica usata dall'invio
 * (testo dell'editor così com'è + intestazione personalizzata + chiusura + firma + footer). Nessuna traduzione/riscrittura.
 */
export function EmailPreviewButton({
  body, subject, label = "Anteprima con firma", className = "", disabled = false,
  nation = null, recipientName = "", informal = false, target = null, contactId = null, campaignId = null,
}) {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState("");
  const [locSubject, setLocSubject] = useState("");
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  const openPreview = async () => {
    setOpen(true);
    setLoading(true);
    setLocSubject("");
    setMeta(null);
    try {
      const { data } = await api.post("/email/preview", {
        body: body || "", subject: subject || "",
        nation: nation || null, recipient_name: recipientName || "", informal: !!informal,
        target: target || null, contact_id: contactId || null, campaign_id: campaignId || null,
      });
      setHtml(data.html || "");
      setLocSubject(data.subject || subject || "");
      setMeta({ lang: data.lang, headerAdded: !!data.header_added });
    } catch {
      setHtml("<p style='font-family:Arial;color:#c8102e'>Errore nel caricamento dell'anteprima.</p>");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button" variant="secondary" size="sm" data-testid="email-preview-btn"
        onClick={openPreview} disabled={disabled}
        className={`rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover ${className}`}
      >
        <Eye className="mr-1 h-3.5 w-3.5" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl border-border bg-brema-card" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Anteprima email</DialogTitle>
            <DialogDescription>
              Da: Giovanna Blasi | Brema Bike
              {locSubject ? <> · Oggetto: <span data-testid="email-preview-subject">{locSubject}</span></> : ""}
              {" — esattamente ciò che riceverà il destinatario: il testo dell'editor così com'è"}
              {meta?.headerAdded ? ", con intestazione personalizzata" : ""}
              {", firma e footer legale. Nessuna modifica o traduzione all'invio."}
              {meta?.lang ? <> <span data-testid="email-preview-lang" className="text-muted-foreground">· Lingua rilevata: {LANG_LABEL[meta.lang] || meta.lang}</span></> : ""}
            </DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground" data-testid="email-preview-loading">
              <Loader2 className="h-5 w-5 animate-spin" /> Caricamento anteprima…
            </div>
          ) : (
            <iframe
              title="Anteprima email" srcDoc={html} data-testid="email-preview-frame"
              className="h-[62vh] w-full rounded-lg border border-border bg-white"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
